import { z } from 'zod';

import {
  emailSchema,
  idSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
} from '../common';
import {
  subscriptionPlanSchema,
  subscriptionStatusSchema,
  userRoleSchema,
  userStatusSchema,
} from '../enums';

/**
 * `/workspace` DTOs — the tenant boundary.
 *
 * Every other resource in the API is scoped to exactly one workspace, resolved
 * from the access token rather than from a request field. That is why these
 * endpoints address "the current workspace" and take no workspace id: a client
 * cannot name a workspace it does not belong to.
 */

export const workspaceSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  ownerUserId: idSchema,
  /** IANA zone; all dashboard date bucketing is done in this zone. */
  timezone: z.string().min(1).max(64),
  locale: z.string().min(2).max(16),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Workspace = z.infer<typeof workspaceSchema>;

// ---------------------------------------------------------------------------
// Subscription
// ---------------------------------------------------------------------------

/**
 * Hard caps enforced server-side. The dashboard reads them to disable controls
 * early, but the API re-checks every one of them — a limit shown in the UI is a
 * courtesy, the limit in the service is the rule.
 */
export const subscriptionLimitsSchema = z.object({
  maxProjects: z.number().int().min(0),
  maxAgents: z.number().int().min(0),
  maxBrowserProfiles: z.number().int().min(0),
  maxKeywordsPerProject: z.number().int().min(0),
  maxPostsPerMonth: z.number().int().min(0),
  maxAiScoringRequestsPerMonth: z.number().int().min(0),
  maxAiCommentRequestsPerMonth: z.number().int().min(0),
  maxCommentsPerDay: z.number().int().min(0),
  maxStorageBytes: z.number().int().min(0),
});
export type SubscriptionLimits = z.infer<typeof subscriptionLimitsSchema>;

/**
 * Consumption for the current billing period. Mirrors the `USAGE_METRICS`
 * vocabulary one field per metric rather than a keyed record: a record typed by
 * the enum would claim every key is present, which the aggregation query cannot
 * guarantee for a workspace that has never used a feature.
 */
export const subscriptionUsageSchema = z.object({
  periodStart: isoDateTimeSchema,
  periodEnd: isoDateTimeSchema,
  postsIngested: z.number().int().min(0),
  aiScoringRequests: z.number().int().min(0),
  aiCommentRequests: z.number().int().min(0),
  commentsSent: z.number().int().min(0),
  searchCycles: z.number().int().min(0),
  storageBytes: z.number().int().min(0),
});
export type SubscriptionUsage = z.infer<typeof subscriptionUsageSchema>;

export const subscriptionSummarySchema = z.object({
  id: idSchema,
  plan: subscriptionPlanSchema,
  status: subscriptionStatusSchema,
  currentPeriodStart: isoDateTimeSchema,
  currentPeriodEnd: isoDateTimeSchema,
  trialEndsAt: isoDateTimeSchema.nullable(),
  cancelAtPeriodEnd: z.boolean(),
  limits: subscriptionLimitsSchema,
  usage: subscriptionUsageSchema,
});
export type SubscriptionSummary = z.infer<typeof subscriptionSummarySchema>;

// ---------------------------------------------------------------------------
// GET /workspace
// ---------------------------------------------------------------------------

export const workspaceDetailResponseSchema = z.object({
  workspace: workspaceSchema,
  subscription: subscriptionSummarySchema,
  memberCount: z.number().int().min(1),
  /** Set while an operator has tripped the global kill switch. */
  emergencyStopActive: z.boolean(),
  emergencyStopAt: isoDateTimeSchema.nullable(),
});
export type WorkspaceDetailResponse = z.infer<typeof workspaceDetailResponseSchema>;

// ---------------------------------------------------------------------------
// PATCH /workspace
// ---------------------------------------------------------------------------

export const updateWorkspaceRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    timezone: z.string().min(1).max(64),
    locale: z.string().min(2).max(16),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateWorkspaceRequest = z.infer<typeof updateWorkspaceRequestSchema>;

export const updateWorkspaceResponseSchema = workspaceSchema;
export type UpdateWorkspaceResponse = z.infer<typeof updateWorkspaceResponseSchema>;

// ---------------------------------------------------------------------------
// GET /workspace/members
// ---------------------------------------------------------------------------

export const workspaceMemberSchema = z.object({
  userId: idSchema,
  email: emailSchema,
  displayName: z.string(),
  role: userRoleSchema,
  status: userStatusSchema,
  joinedAt: isoDateTimeSchema,
  lastActiveAt: isoDateTimeSchema.nullable(),
});
export type WorkspaceMember = z.infer<typeof workspaceMemberSchema>;

export const workspaceMemberListQuerySchema = paginationQuerySchema.extend({
  role: userRoleSchema.optional(),
  status: userStatusSchema.optional(),
  search: z.string().trim().max(120).optional(),
});
export type WorkspaceMemberListQuery = z.infer<typeof workspaceMemberListQuerySchema>;

export const workspaceMemberListResponseSchema = paginated(workspaceMemberSchema);
export type WorkspaceMemberListResponse = z.infer<typeof workspaceMemberListResponseSchema>;

// ---------------------------------------------------------------------------
// GET /workspace/subscription
// ---------------------------------------------------------------------------

export const subscriptionResponseSchema = z.object({
  subscription: subscriptionSummarySchema,
});
export type SubscriptionResponse = z.infer<typeof subscriptionResponseSchema>;
